import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { LeadWarning, WarningItem } from '@/types/warnings'
import useAuthStore from '@/stores/useAuthStore'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { resources, scopes } from '@/utils/permission'
import {
    fetchWarningsForScopes,
    getScopesByDepartment,
    WARNING_TYPE_ORDER,
} from './services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getCanonicalDepartmentName } from '@/constants/departments'

// Chỉ hiển thị cảnh báo lead (chưa nhận / chưa phân loại) cho lead có ngày
// từ 01/01/2026 trở đi — ẩn lead cũ. Mốc ngày lấy từ warning.created_at:
// unassigned = ngày tạo lead, stale = ngày được gán.
const LEAD_ALERT_START_DATE = new Date('2026-01-01T00:00:00')

// Nhóm lead luôn hiển thị (kể cả khi rỗng sau khi lọc) → cần title fallback
// vì nhóm rỗng không có item để lấy title từ backend. Values are i18n keys.
export const LEAD_GROUP_FALLBACK_TITLE: Record<string, string> = {
    lead_unassigned: 'alerts.lead-fallback.lead_unassigned',
    lead_stale: 'alerts.lead-fallback.lead_stale',
}

/** Warnings for the current user, shared by the topbar button and the side panel. */
export const useAlertNotifications = () => {
    const { employee } = useAuthStore()
    const departmentName = getCanonicalDepartmentName(employee)
    const { permissions, isAdmin } = usePermissionsStore()

    const canViewCustomerEvents = useMemo(
        () =>
            isAdmin ||
            permissions.some(
                (permission) =>
                    permission.rsname === resources.customers &&
                    permission.scopes?.includes(scopes.create)
            ),
        [isAdmin, permissions]
    )

    // Department mappings intentionally cover all legacy warning sources only.
    // Customer events are granted solely by Admin or Customers:create.
    const allowedScopes = useMemo(() => {
        const departmentScopes = getScopesByDepartment(departmentName)
        return canViewCustomerEvents
            ? [
                  ...new Set<WarningItem['type']>([
                      ...departmentScopes,
                      'customer_event_upcoming',
                  ]),
              ]
            : departmentScopes
    }, [canViewCustomerEvents, departmentName])

    // Fetch warnings data based on allowed scopes
    const {
        data: warningResult,
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: [QUERY_KEYS.WARNINGS, allowedScopes],
        queryFn: () => fetchWarningsForScopes(allowedScopes),
        refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        retry: 2,
        staleTime: 5 * 60 * 1000,
        enabled: allowedScopes.length > 0, // Only fetch if user has allowed scopes
    })

    const alerts = warningResult?.warnings ?? []
    const failedSources = warningResult?.failedSources ?? []

    // Lọc cảnh báo lead theo mốc 01/01/2026 (chỉ lead từ 2026 trở đi).
    // Lọc tại nguồn để badge count và danh sách luôn khớp nhau.
    const filteredAlerts = useMemo(() => {
        return (alerts ?? []).filter((alert) => {
            if (
                alert.type === 'lead_unassigned' ||
                alert.type === 'lead_stale'
            ) {
                const d = new Date(alert.created_at)
                return !isNaN(d.getTime()) && d >= LEAD_ALERT_START_DATE
            }
            return true
        })
    }, [alerts])

    // Group alerts by type and sort according to priority order
    const groupedAlerts = useMemo(() => {
        const groups: Record<string, WarningItem[]> = {}
        filteredAlerts.forEach((alert) => {
            if (!groups[alert.type]) {
                groups[alert.type] = []
            }
            groups[alert.type].push(alert)
        })

        // Luôn giữ nhóm lead (nếu thuộc scope) kể cả khi rỗng — để mục
        // "Leads chưa nhận > 24h" / "Leads chưa phân loại > 24h" vẫn hiển thị
        // (đếm = 0) sau khi đã lọc bỏ lead cũ trước 2026.
        const alwaysShowLeadTypes: WarningItem['type'][] = [
            'lead_unassigned',
            'lead_stale',
        ]
        alwaysShowLeadTypes.forEach((type) => {
            if (allowedScopes.includes(type) && !groups[type]) {
                groups[type] = []
            }
        })

        // Sort each group
        Object.keys(groups).forEach((type) => {
            if (type === 'lead_unassigned' || type === 'lead_stale') {
                // Lead alerts: sắp theo thời gian chờ TĂNG DẦN — lead vừa qua
                // ngưỡng 24h đứng đầu, lead chờ lâu hơn xuống dưới.
                groups[type].sort((a, b) => {
                    const aDays = (a.data as LeadWarning)?.days_pending ?? 0
                    const bDays = (b.data as LeadWarning)?.days_pending ?? 0
                    return aDays - bDays
                })
            } else {
                // Các cảnh báo khác: giữ nguyên — sort theo severity (high trước)
                groups[type].sort((a, b) => {
                    const severityOrder = { high: 0, medium: 1, low: 2 }
                    return severityOrder[a.severity] - severityOrder[b.severity]
                })
            }
        })

        // Sort groups by WARNING_TYPE_ORDER
        const sortedGroups: Record<string, WarningItem[]> = {}
        WARNING_TYPE_ORDER.forEach((type) => {
            if (groups[type]) {
                sortedGroups[type] = groups[type]
            }
        })

        // Add any remaining types not in the order
        Object.keys(groups).forEach((type) => {
            if (!sortedGroups[type]) {
                sortedGroups[type] = groups[type]
            }
        })

        return sortedGroups
    }, [filteredAlerts, allowedScopes])

    const highPriorityCount = filteredAlerts.filter(
        (alert) => alert.severity === 'high'
    ).length
    const totalCount = filteredAlerts.length

    return {
        allowedScopes,
        isLoading,
        isError,
        error,
        failedSources,
        groupedAlerts,
        totalCount,
        highPriorityCount,
    }
}
